/**
 * ApiKeyManagement — TASK-022 / flow-04
 * Actor: sys_admin, biz_admin
 * Route: /admin/api-keys
 */
import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AppBadge, AppButton, AppCard, AppField, AppInput, AppModal, ConfirmAction } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconCheck, IconSpinner, IconWarning } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";

type ApiKey = {
  id: number;
  name: string;
  key_prefix: string;
  permissions: string[];
  is_active: boolean;
  created_at: string;
  last_used: string | null;
  usage_count?: number;
  expires_at?: string | null;
};

export function ApiKeyManagement() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [pageState, setPageState] = useState<"loading"|"error"|"success">("loading");
  const [pageError, setPageError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState("");
  const [keyName, setKeyName] = useState("");
  const [busy, setBusy] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [actionBusy, setActionBusy] = useState<number | null>(null);
  const [confirmKey, setConfirmKey] = useState<ApiKey | null>(null);

  const load = async () => {
    setPageState("loading");
    try {
      const d = await api.get<{ api_keys: ApiKey[] }>("/api-keys");
      setKeys(d.api_keys ?? []);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load API keys");
      setPageState("error");
    }
  };
  useEffect(() => { load(); }, []);

  const createKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyName.trim()) { setCreateErr("Name is required"); return; }
    setBusy(true); setCreateErr("");
    try {
      const d = await api.post<{ api_key: string }>("/api-keys", { name: keyName });
      setNewKeyValue(d.api_key);
      setKeyName("");
      await load();
    } catch (ex: unknown) {
      setCreateErr(ex instanceof Error ? ex.message : "Failed");
    }
    finally { setBusy(false); }
  };

  const toggleKey = async (k: ApiKey) => {
    setActionBusy(k.id);
    try {
      await api.post(`/api-keys/${k.id}/${k.is_active ? "deactivate" : "activate"}`, {});
      await load();
    } finally { setActionBusy(null); }
  };

  const columns: ColumnDef<ApiKey, unknown>[] = [
    { accessorKey: "name", header: "Name" },
    { id: "prefix", header: "Key Prefix", accessorFn: (r) => `${r.key_prefix}…` },
    { id: "status", header: "Status", cell: ({ row }) => row.original.is_active ? <AppBadge tone="success"><IconCheck size={12}/> Active</AppBadge> : <AppBadge tone="neutral">Inactive</AppBadge> },
    { id: "last_used", header: "Last Used", accessorFn: (r) => r.last_used ? new Date(r.last_used).toLocaleDateString("en-GB") : "Never" },
    { id: "created_at", header: "Created", accessorFn: (r) => new Date(r.created_at).toLocaleDateString("en-GB") },
    { id: "actions", header: "", enableSorting: false, cell: ({ row }) => (
      <AppButton type="button" variant="secondary" disabled={actionBusy === row.original.id}
        onClick={(e) => { e.stopPropagation(); setConfirmKey(row.original); }}>
        {row.original.is_active ? "Deactivate" : "Activate"}
      </AppButton>
    )},
  ];

  const listStatus =
    pageState === "success" && keys.length === 0 ? "empty" : pageState;

  return (
    <>
      <AppModal open={createOpen} onClose={() => { setCreateOpen(false); setNewKeyValue(""); }} title="Create API Key">
        {newKeyValue ? (
          <>
            <div className="pg-token-display"><code id="new-api-key-value">{newKeyValue}</code></div>
            <p className="pg-helper-text"><IconWarning size={14}/> Copy this key now — it will not be shown again.</p>
            <div className="pg-modal-actions">
              <AppButton type="button" onClick={() => { navigator.clipboard.writeText(newKeyValue); }}>Copy Key</AppButton>
              <AppButton type="button" variant="secondary" onClick={() => { setCreateOpen(false); setNewKeyValue(""); }}>Close</AppButton>
            </div>
          </>
        ) : (
          <form onSubmit={createKey}>
            <AppField label="Key Name" error={createErr}>
              <AppInput id="apikey-name" type="text" placeholder="e.g. Mobile App Production" value={keyName}
                onChange={(e) => setKeyName(e.target.value)} disabled={busy} autoFocus />
            </AppField>
            <div className="pg-modal-actions">
              <AppButton type="submit" disabled={busy}>{busy ? <><IconSpinner size={14}/> Creating…</> : "Generate Key"}</AppButton>
              <AppButton type="button" variant="secondary" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</AppButton>
            </div>
          </form>
        )}
      </AppModal>

      <PageHeader
        title="API Keys"
        subtitle="Integration keys for external systems."
        actions={<AppButton type="button" onClick={() => setCreateOpen(true)}>Generate Key</AppButton>}
      />

      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No API keys"
        emptyHint="Generate a key for service-to-service access."
      >
        <AppCard><AppDataTable data={keys} columns={columns} /></AppCard>
      </PageState>

      <ConfirmAction
        open={!!confirmKey}
        title={confirmKey?.is_active ? "Deactivate API key?" : "Activate API key?"}
        message={
          confirmKey
            ? confirmKey.is_active
              ? `${confirmKey.name} (${confirmKey.key_prefix}…) will stop working until reactivated.`
              : `Re-enable ${confirmKey.name}?`
            : ""
        }
        confirmLabel={confirmKey?.is_active ? "Deactivate" : "Activate"}
        danger={!!confirmKey?.is_active}
        busy={actionBusy === confirmKey?.id}
        onCancel={() => setConfirmKey(null)}
        onConfirm={() => {
          const k = confirmKey;
          setConfirmKey(null);
          if (k) void toggleKey(k);
        }}
      />
    </>
  );
}
